import { initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics";
import {
  type Auth,
  browserLocalPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";

const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  ...(measurementId ? { measurementId } : {}),
};

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let analyticsInstance: Analytics | null = null;
let analyticsInitDone = false;

/** apiKey と projectId が揃っているときだけ true（未設定時は Auth の useEffect が止まらないようにする） */
export function isFirebaseConfigured(): boolean {
  return Boolean(config.apiKey && config.projectId);
}

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    if (!isFirebaseConfigured()) {
      throw new Error(
        "Firebase の設定がありません。.env に VITE_FIREBASE_API_KEY と VITE_FIREBASE_PROJECT_ID などを設定してください。",
      );
    }
    app = initializeApp(config);
  }
  return app;
}

export function getFirebaseAuth(): Auth {
  if (!authInstance) {
    const a = getFirebaseApp();
    try {
      authInstance = initializeAuth(a, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      });
    } catch (e: unknown) {
      const code =
        typeof e === "object" && e !== null && "code" in e ? String((e as { code: unknown }).code) : "";
      if (code === "auth/already-initialized") {
        authInstance = getAuth(a);
      } else {
        throw e;
      }
    }
  }
  return authInstance;
}

/** Console の `getAnalytics(app)` に相当。未対応環境・ブロッカー時は何もしない */
export async function initFirebaseAnalytics(): Promise<void> {
  if (analyticsInitDone) return;
  analyticsInitDone = true;
  if (!measurementId?.trim()) return;
  if (typeof window === "undefined") return;
  try {
    if (!(await isSupported())) return;
    analyticsInstance = getAnalytics(getFirebaseApp());
  } catch {
    analyticsInstance = null;
  }
}

export function getFirebaseAnalytics(): Analytics | null {
  return analyticsInstance;
}
